import React from 'react';
import { StyleSheet, Text, TouchableOpacity, View } from 'react-native';
import { recordError } from '@common/services/featherIO/crashLogger';
import appConfig from '../../../appConfig.json';

interface Props {
  children: React.ReactNode;
}

interface State {
  hasError: boolean;
}

const ERROR_TITLE = '問題が発生しました';
const ERROR_MESSAGE = '通信環境をご確認のうえ、再度お試しください。';
const RETRY_LABEL = '再試行';

/**
 * Root-level safety net. Catches render-time JS errors (e.g. failures triggered
 * while the device is offline) so the whole app doesn't crash, and offers the
 * user a retry. Errors are still logged, so Firebase Crashlytics can record them
 * through the JS exception handler configured in firebase.json.
 */
class ErrorBoundary extends React.Component<Props, State> {
  state: State = { hasError: false };

  static getDerivedStateFromError(): State {
    return { hasError: true };
  }

  componentDidCatch(error: Error, info: React.ErrorInfo): void {
    console.error('ErrorBoundary caught an error:', error, info);
    // Report the caught render error to Firebase Crashlytics, tagged with
    // app_id (native only; no-op on web).
    recordError(error, appConfig.appId);
  }

  handleRetry = (): void => {
    this.setState({ hasError: false });
  };

  render(): React.ReactNode {
    if (!this.state.hasError) {
      return this.props.children;
    }

    return (
      <View style={styles.container}>
        <Text style={styles.title}>{ERROR_TITLE}</Text>
        <Text style={styles.message}>{ERROR_MESSAGE}</Text>
        <TouchableOpacity
          style={styles.button}
          onPress={this.handleRetry}
          activeOpacity={0.8}
        >
          <Text style={styles.buttonText}>{RETRY_LABEL}</Text>
        </TouchableOpacity>
      </View>
    );
  }
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    paddingHorizontal: 32,
    backgroundColor: 'white',
  },
  title: {
    fontSize: 18,
    fontWeight: '600',
    color: '#333',
    marginBottom: 12,
    textAlign: 'center',
  },
  message: {
    fontSize: 14,
    color: '#666',
    marginBottom: 24,
    textAlign: 'center',
    lineHeight: 20,
  },
  button: {
    paddingVertical: 12,
    paddingHorizontal: 32,
    borderRadius: 8,
    backgroundColor: '#333',
  },
  buttonText: {
    color: 'white',
    fontSize: 14,
    fontWeight: '600',
  },
});

export default ErrorBoundary;
